import { useCallback, useState, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import Camera01Icon from '@untitled-ui/icons-react/build/esm/Camera01';
import Send01Icon from '@untitled-ui/icons-react/build/esm/Send01';
import CloseIcon from '@untitled-ui/icons-react/build/esm/XClose';
import { Box, Chip, IconButton, OutlinedInput, Stack, styled, SvgIcon, Tooltip, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BRAND, RADIUS } from 'src/theme/ctmass-tokens';
import { tr } from 'src/locales/tr';

const ScrollableBox = styled(Box)({
    display: 'flex',
    overflowX: 'auto',
    gap: '8px',
    padding: '8px 0',
    justifyContent: 'center',
    '&::-webkit-scrollbar': {
        height: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#888',
        borderRadius: '2px',
    },
});

export const MessengerMessageAdd = (props) => {
    const { disabled, templatesEnabled = false, onSend, isSending = false, inputRef, ...other } = props;
    const [body, setBody] = useState('');
    const [files, setFiles] = useState([]);
    const mdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

    const templateMessages = [
        {
            text: tr("Submit"),
            color: 'success.main',
            onClick: () => {
                console.log('submit clicked');
                setBody('submit!');
            },
        },
        {
            text: tr("Reject"),
            color: 'error.main',
            onClick: () => {
                console.log('Reject clicked');
                setBody('Reject');
            },
        },
    ];

    const localRef = useRef(null);
    useImperativeHandle(inputRef, () => localRef.current);

    const handleChange = (event) => {
        setBody(event.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleAttachFiles = () => {
        if (isSending) return; // Блокируем выбор файлов во время отправки

        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = 'image/*';
        input.onchange = (e) => {
            const selectedFiles = Array.from(e.target.files);
            const validFiles = selectedFiles.filter((file) => file.size <= 5 * 1024 * 1024); // Фильтруем файлы по размеру (5 Мб)
            if (validFiles.length > 5) {
                alert(tr("You can upload up to 5 files."));
                return;
            }
            if (validFiles.length !== selectedFiles.length) {
                alert(tr("Some files exceed the allowed size (5 MB)"));
            }
            setFiles((prevFiles) => [...prevFiles, ...validFiles].slice(0, 5)); // Ограничиваем количество файлов до 5
        };
        input.click();
    };

    const handleRemoveFile = (index) => {
        if (isSending) return; // Блокируем удаление файлов во время отправки
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const handleSend = useCallback(() => {
        if ((!body && files.length === 0) || isSending) return; // Блокируем отправку, если идет отправка

        onSend?.(body, files);
        setBody('');
        setFiles([]);
    }, [body, files, onSend, isSending]);

    return (
        <Box>
            {templatesEnabled && (
                <ScrollableBox>
                    {templateMessages.map((template, index) => (
                        <Chip
                            key={index}
                            label={template.text}
                            onClick={template.onClick}
                            sx={{
                                cursor: 'pointer',
                                backgroundColor: template.color,
                                width: '15%',
                                color: 'common.white',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                    backgroundColor: template.color,
                                    transform: 'scale(1.1)',
                                },
                            }}
                        />
                    ))}
                </ScrollableBox>
            )}

            {files.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, p: 2, overflowX: 'auto' }}>
                    {files.map((file, index) => (
                        <Box key={index} sx={{ position: 'relative', opacity: isSending ? 0.5 : 1 }}>
                            <img
                                src={URL.createObjectURL(file)}
                                alt={tr("file-{{0}}", { 0: index })}
                                style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                            />
                            {!isSending && (
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        },
                                    }}
                                    onClick={() => handleRemoveFile(index)}
                                >
                                    <SvgIcon fontSize="small">
                                        <CloseIcon />
                                    </SvgIcon>
                                </IconButton>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            <Stack
                alignItems="flex-end"
                direction="row"
                spacing={1}
                sx={{
                    px: mdUp ? 2 : 1.5,
                    py: 1.25,
                    bgcolor: '#FFFFFF'
                }}
                {...other}
            >
                <OutlinedInput
                    disabled={disabled || isSending} // Блокируем поле ввода во время отправки
                    fullWidth
                    multiline
                    maxRows={4}
                    inputRef={localRef}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={tr("Write a message")}
                    size="small"
                    autoFocus={mdUp}
                    value={body}
                    sx={{
                        borderRadius: '20px',
                        bgcolor: BRAND.mist,
                        py: 1,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(BRAND.navy, 0.14) },
                        '&.Mui-focused': { bgcolor: '#FFFFFF', boxShadow: `0 0 0 4px ${alpha(BRAND.green, 0.14)}` },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(BRAND.green, 0.6), borderWidth: 1 }
                    }}
                />
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        gap: 0.5,
                        flexShrink: 0
                    }}
                >
                    <Tooltip title={tr("Send")}>
                        <Box>
                            <IconButton
                                aria-label={tr("Send")}
                                disabled={(!body && files.length === 0) || disabled || isSending}
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: RADIUS.tile,
                                    backgroundColor: BRAND.green,
                                    color: '#FFFFFF',
                                    '&:hover': { backgroundColor: '#119A55' },
                                    '&.Mui-disabled': { backgroundColor: alpha(BRAND.navy, 0.08), color: alpha(BRAND.navy, 0.35) }
                                }}
                                onClick={handleSend}
                            >
                                <SvgIcon>
                                    <Send01Icon />
                                </SvgIcon>
                            </IconButton>
                        </Box>
                    </Tooltip>
                    <Tooltip title={tr("Attach files")}>
                        <Box sx={{ display: 'inline-flex' }}>
                            <IconButton
                                aria-label={tr("Attach files")}
                                disabled={disabled || isSending}
                                onClick={handleAttachFiles}
                                sx={{ width: 44, height: 44, borderRadius: RADIUS.tile, color: BRAND.navy, '&:hover': { bgcolor: alpha(BRAND.navy, 0.06) } }}
                            >
                                <SvgIcon>
                                    <Camera01Icon />
                                </SvgIcon>
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Stack>
        </Box>
    );
};

MessengerMessageAdd.propTypes = {
    disabled: PropTypes.bool,
    onSend: PropTypes.func,
    isSending: PropTypes.bool,
};

MessengerMessageAdd.defaultProps = {
    disabled: false,
    isSending: false,
};